/* =========================================================
   Lunora Jewelry – theme.js
   Vanilla JS, no dependencies. Modules:
   utils · overlay/drawers · header · announcement · carousel ·
   tabs · collapsible · reveal · cart API · cart drawer/page ·
   quick add · product form · gallery/hotspots · sticky bar ·
   collection filters · predictive search · newsletter · look
   ========================================================= */
(() => {
  'use strict';
  const T = window.theme || { routes: {}, settings: {}, strings: {} };
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const debounce = (fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  /* ---------- Money ---------- */
  const formatMoney = (cents) => {
    const format = T.moneyFormat || '{{amount}} €';
    const value = (cents / 100);
    const decimals = /no_decimals/.test(format) ? 0 : 2;
    let str;
    if (/amount_with_comma_separator/.test(format)) {
      str = value.toFixed(decimals).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    } else if (/amount_no_decimals_with_comma_separator/.test(format)) {
      str = value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    } else {
      str = value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, str).replace(/<[^>]+>/g, '');
  };

  /* ---------- Toast ---------- */
  let toastEl;
  const toast = (msg) => {
    if (!toastEl) { toastEl = document.createElement('div'); toastEl.className = 'toast'; document.body.appendChild(toastEl); }
    toastEl.textContent = msg; toastEl.classList.add('is-visible');
    clearTimeout(toastEl._t); toastEl._t = setTimeout(() => toastEl.classList.remove('is-visible'), 2400);
  };

  /* =========================================================
     Overlay & drawers
     ========================================================= */
  const overlay = $('[data-overlay]');
  let openEl = null;
  const focusables = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  const openDrawer = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (openEl && openEl !== el) closeDrawer(true);
    openEl = el;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    if (overlay) { overlay.hidden = false; requestAnimationFrame(() => overlay.classList.add('is-visible')); }
    const first = el.querySelector('[data-autofocus]') || el.querySelector(focusables);
    setTimeout(() => first && first.focus({ preventScroll: true }), 120);
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
  };
  const closeDrawer = (keepLock) => {
    if (!openEl) return;
    openEl.classList.remove('is-open');
    openEl.setAttribute('aria-hidden', 'true');
    openEl = null;
    if (!keepLock) {
      document.body.classList.remove('is-locked');
      if (overlay) { overlay.classList.remove('is-visible'); setTimeout(() => { if (!openEl) overlay.hidden = true; }, 300); }
    }
  };
  on(overlay, 'click', () => closeDrawer());
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
    if (e.key === 'Tab' && openEl) {
      const items = $$(focusables, openEl).filter(i => i.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
  on(document, 'click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const id = opener.dataset.drawerOpen;
      if (id === 'CartDrawer' && T.settings.cartType !== 'drawer') return;
      e.preventDefault(); openDrawer(id); return;
    }
    if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); closeDrawer(); }
  });
  window.theme.openDrawer = openDrawer;
  window.theme.closeDrawer = closeDrawer;

  /* =========================================================
     Header
     ========================================================= */
  const headerWrap = $('[data-header]');
  if (headerWrap) {
    const setScrolled = () => headerWrap.classList.toggle('is-scrolled', window.scrollY > 8);
    on(window, 'scroll', setScrolled, { passive: true }); setScrolled();
    const setHeight = () => document.documentElement.style.setProperty('--header-height', headerWrap.offsetHeight + 'px');
    on(window, 'resize', debounce(setHeight, 100)); setHeight();
  }
  // Menu drawer accordions
  on(document, 'click', (e) => {
    const t = e.target.closest('[data-submenu-toggle]');
    if (!t) return;
    const sub = t.nextElementSibling;
    const open = !sub.classList.contains('is-open');
    sub.classList.toggle('is-open', open);
    t.setAttribute('aria-expanded', String(open));
  });
  // Footer accordions (mobile)
  on(document, 'click', (e) => {
    const t = e.target.closest('[data-footer-toggle]');
    if (!t || window.innerWidth > 860) return;
    e.preventDefault();
    const list = t.nextElementSibling;
    const open = !list.classList.contains('is-open');
    list.classList.toggle('is-open', open);
    t.setAttribute('aria-expanded', String(open));
  });

  /* =========================================================
     Announcement rotation (mobile)
     ========================================================= */
  $$('[data-announcement]').forEach(bar => {
    const items = $$('.announcement__item', bar);
    if (items.length < 2) { items[0] && items[0].classList.add('is-active'); return; }
    let i = 0; items[0].classList.add('is-active');
    setInterval(() => {
      if (window.innerWidth > 860) return;
      items[i].classList.remove('is-active'); i = (i + 1) % items.length; items[i].classList.add('is-active');
    }, Number(bar.dataset.interval || 3500));
  });

  /* =========================================================
     Carousel (custom element)
     ========================================================= */
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-carousel-track]', this);
      if (!this.track) return;
      this.prev = $('[data-carousel-prev]', this);
      this.next = $('[data-carousel-next]', this);
      this.dots = $('[data-carousel-dots]', this);
      this.slides = () => $$('.carousel__slide', this.track);
      on(this.prev, 'click', () => this.scrollBy(-1));
      on(this.next, 'click', () => this.scrollBy(1));
      on(this.track, 'scroll', debounce(() => this.update(), 60), { passive: true });
      on(window, 'resize', debounce(() => { this.buildDots(); this.update(); }, 150));
      this.buildDots(); this.update();
      if (this.dataset.autoplay) this.autoplay(Number(this.dataset.autoplay));
      // Drag to scroll (desktop)
      let down = false, startX = 0, startLeft = 0, moved = false;
      on(this.track, 'pointerdown', (e) => { if (e.pointerType !== 'mouse') return; down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft; this.track.style.scrollSnapType = 'none'; this.track.style.scrollBehavior = 'auto'; });
      on(window, 'pointermove', (e) => { if (!down) return; const dx = e.clientX - startX; if (Math.abs(dx) > 4) moved = true; this.track.scrollLeft = startLeft - dx; });
      on(window, 'pointerup', () => { if (!down) return; down = false; this.track.style.scrollSnapType = ''; this.track.style.scrollBehavior = ''; if (moved) { this.snap(); this.track.addEventListener('click', (ev) => ev.preventDefault(), { once: true, capture: true }); } });
    }
    slideWidth() { const s = this.slides()[0]; return s ? s.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0) : 0; }
    perView() { const w = this.slideWidth(); return w ? Math.max(1, Math.round(this.track.clientWidth / w)) : 1; }
    pages() { return Math.max(1, Math.ceil(this.slides().length / this.perView())); }
    currentPage() { const w = this.slideWidth(); return w ? Math.round(this.track.scrollLeft / (w * this.perView())) : 0; }
    scrollTo(page) { this.track.scrollTo({ left: page * this.slideWidth() * this.perView(), behavior: 'smooth' }); }
    scrollBy(dir) {
      const pages = this.pages(); let next = this.currentPage() + dir;
      if (next < 0) next = this.dataset.loop ? pages - 1 : 0;
      if (next > pages - 1) next = this.dataset.loop ? 0 : pages - 1;
      this.scrollTo(next);
    }
    snap() { this.goTo(Math.round(this.track.scrollLeft / (this.slideWidth() || 1))); }
    buildDots() {
      if (!this.dots) return;
      const pages = this.pages(); this.dots.innerHTML = '';
      if (pages < 2) return;
      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'carousel__dot'; b.setAttribute('aria-label', `${i + 1}/${pages}`);
        on(b, 'click', () => this.scrollTo(i)); this.dots.appendChild(b);
      }
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev && !this.dataset.loop) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next && !this.dataset.loop) this.next.disabled = this.track.scrollLeft >= max;
      if (this.prev && this.next && max <= 0) { this.prev.disabled = true; this.next.disabled = true; }
      if (this.dots) { const cur = this.currentPage(); $$('.carousel__dot', this.dots).forEach((d, i) => d.classList.toggle('is-active', i === cur)); }
      this.dispatchEvent(new CustomEvent('carousel:change', { detail: { page: this.currentPage() } }));
    }
    autoplay(ms) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const tick = () => { if (!this.paused && this.pages() > 1) { const p = this.currentPage(); this.scrollTo(p >= this.pages() - 1 ? 0 : p + 1); } };
      this._timer = setInterval(tick, ms);
      on(this, 'pointerenter', () => this.paused = true); on(this, 'pointerleave', () => this.paused = false);
      on(this, 'focusin', () => this.paused = true); on(this, 'focusout', () => this.paused = false);
    }
    goTo(index) { const w = this.slideWidth(); this.track.scrollTo({ left: index * w, behavior: 'smooth' }); }
  }
  if (!customElements.get('carousel-slider')) customElements.define('carousel-slider', CarouselSlider);

  /* =========================================================
     Tabs
     ========================================================= */
  on(document, 'click', (e) => {
    const tab = e.target.closest('[data-tab]');
    if (!tab) return;
    const root = tab.closest('[data-tabs]');
    $$('[data-tab]', root).forEach(t => { t.classList.toggle('is-active', t === tab); t.setAttribute('aria-selected', String(t === tab)); });
    $$('[data-tab-panel]', root).forEach(p => { p.hidden = p.dataset.tabPanel !== tab.dataset.tab; });
    $$('carousel-slider', root).forEach(c => c.update && c.update());
  });

  /* ---------- Collapsible ---------- */
  on(document, 'click', (e) => {
    const t = e.target.closest('[data-collapsible-toggle]');
    if (!t) return;
    const root = t.closest('[data-collapsible]');
    const open = root.classList.toggle('is-open');
    t.setAttribute('aria-expanded', String(open));
    if (!open) root.scrollIntoView({ block: 'start', behavior: 'smooth' });
  });
  $$('[data-collapsible]').forEach(c => {
    const body = $('.collapsible__body', c);
    const limit = parseInt(getComputedStyle(body).getPropertyValue('--collapsed-height')) || 260;
    if (body && body.scrollHeight <= limit + 40) { c.classList.add('is-open'); const t = $('[data-collapsible-toggle]', c); if (t) t.hidden = true; }
  });

  /* ---------- Reveal on scroll ---------- */
  if (T.settings.reveal !== false && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px', threshold: .05 });
    const observe = (root = document) => $$('.reveal:not(.is-visible)', root).forEach(el => io.observe(el));
    observe();
    document.addEventListener('shopify:section:load', (e) => observe(e.target));
    window.theme.observeReveal = observe;
  } else { $$('.reveal').forEach(el => el.classList.add('is-visible')); }

  /* =========================================================
     Cart API
     ========================================================= */
  const cartSections = () => ['cart-drawer', 'cart-icon-bubble'].concat($('[data-cart-page]') ? ['main-cart'] : []);
  const renderSections = (sections) => {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const drawer = $('[data-cart-drawer]');
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = $('[data-cart-drawer-content]', html);
      const target = $('[data-cart-drawer-content]', drawer);
      if (fresh && target) target.replaceWith(fresh);
      const cnt = $('[data-cart-drawer]', html);
      if (drawer && cnt) drawer.dataset.itemCount = cnt.dataset.itemCount;
      $$('carousel-slider', drawer).forEach(c => c.connectedCallback && c.connectedCallback());
    }
    if (sections['cart-icon-bubble']) {
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const count = $('[data-cart-count]', html);
      $$('[data-cart-count]').forEach(el => { if (count) { el.textContent = count.textContent; el.dataset.count = count.dataset.count; } });
    }
    if (sections['main-cart']) {
      const html = new DOMParser().parseFromString(sections['main-cart'], 'text/html');
      const fresh = $('[data-cart-page-content]', html), target = $('[data-cart-page-content]');
      if (fresh && target) target.replaceWith(fresh);
    }
  };
  const cartRequest = async (url, body) => {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
    return data;
  };
  const Cart = {
    async add(items, { open = true } = {}) {
      const data = await cartRequest(T.routes.cartAdd + '.js', { items, sections: cartSections().join(','), sections_url: window.location.pathname });
      renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      if (open) { if (T.settings.cartType === 'drawer') openDrawer('CartDrawer'); else window.location.href = T.routes.cart; }
      return data;
    },
    async change(line, quantity) {
      const data = await cartRequest(T.routes.cartChange + '.js', { line, quantity, sections: cartSections().join(','), sections_url: window.location.pathname });
      renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    async note(note) { return cartRequest(T.routes.cartUpdate + '.js', { note }); }
  };
  window.theme.cart = Cart;

  // Quantity buttons (generic)
  on(document, 'click', (e) => {
    const b = e.target.closest('[data-qty-change]');
    if (!b) return;
    const wrap = b.closest('[data-quantity]');
    const input = wrap && $('input', wrap);
    if (!input) return;
    const min = Number(input.min || 0);
    const next = Math.max(min, Number(input.value || 0) + Number(b.dataset.qtyChange));
    input.value = next; input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  // Cart line changes
  const setCartLoading = (state) => { $$('[data-cart-drawer],[data-cart-page]').forEach(el => el.classList.toggle('is-loading', state)); };
  on(document, 'change', async (e) => {
    const input = e.target.closest('[data-cart-line-qty]');
    if (!input) return;
    setCartLoading(true);
    try { await Cart.change(Number(input.dataset.line), Number(input.value)); } catch (err) { toast(err.message); } finally { setCartLoading(false); }
  });
  on(document, 'click', async (e) => {
    const b = e.target.closest('[data-cart-remove]');
    if (!b) return;
    e.preventDefault(); setCartLoading(true);
    try { await Cart.change(Number(b.dataset.cartRemove), 0); } catch (err) { toast(err.message); } finally { setCartLoading(false); }
  });
  on(document, 'change', debounce((e) => { const n = e.target.closest('[data-cart-note]'); if (n) Cart.note(n.value); }, 400));

  // Generic product form submit (AJAX add)
  on(document, 'submit', async (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    e.preventDefault();
    const btn = $('[data-submit]', form) || $('[type="submit"]', form);
    const fd = new FormData(form);
    const items = [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }];
    const props = {}; for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
    if (Object.keys(props).length) items[0].properties = props;
    if (fd.get('selling_plan')) items[0].selling_plan = Number(fd.get('selling_plan'));
    btn && btn.classList.add('is-loading');
    try {
      await Cart.add(items);
      if (btn) { btn.classList.remove('is-loading'); const orig = btn.textContent; btn.textContent = T.strings.added; setTimeout(() => btn.textContent = orig, 1600); }
      closeIfQuickAdd(form);
    } catch (err) { btn && btn.classList.remove('is-loading'); toast(err.message); }
  });
  const closeIfQuickAdd = (form) => { const qa = form.closest('#QuickAddDrawer'); if (qa && T.settings.cartType !== 'drawer') closeDrawer(); };

  /* =========================================================
     Quick add
     ========================================================= */
  on(document, 'click', async (e) => {
    const b = e.target.closest('[data-quick-add]');
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    if (b.dataset.variantId) {
      b.classList.add('is-loading');
      try { await Cart.add([{ id: Number(b.dataset.variantId), quantity: 1 }]); } catch (err) { toast(err.message); } finally { b.classList.remove('is-loading'); }
      return;
    }
    const drawer = $('#QuickAddDrawer'); const body = $('[data-quick-add-body]', drawer);
    if (!drawer) { window.location.href = b.dataset.url; return; }
    body.innerHTML = '<div class="quick-add__loading"><div class="spinner"></div></div>';
    openDrawer('QuickAddDrawer');
    try {
      const res = await fetch(`${b.dataset.url.split('?')[0]}?section_id=quick-add`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = $('[data-quick-add-content]', doc);
      body.innerHTML = content ? content.outerHTML : html;
      $$('[data-product-form]', body).forEach(el => new ProductForm(el));
    } catch (err) { body.innerHTML = `<p class="muted">${err.message}</p>`; }
  });

  /* =========================================================
     Product form
     ========================================================= */
  class ProductForm {
    constructor(root) {
      if (root._pf) return root._pf; root._pf = this;
      this.root = root; this.sectionId = root.dataset.sectionId;
      this.form = $('form[action*="/cart/add"]', root);
      this.variants = JSON.parse(($('[data-variant-json]', root) || { textContent: '[]' }).textContent);
      this.idInput = $('[name="id"]', root);
      this.price = $('[data-price]', root); this.compare = $('[data-compare]', root); this.unit = $('[data-unit-price]', root); this.savePct = $('[data-save-percent]', root);
      this.submit = $('[data-submit]', root); this.stock = $('[data-stock]', root);
      this.qty = $('[data-quantity-input]', root);
      this.optionInputs = $$('[data-option-input]', root);
      this.gallery = document.querySelector(`[data-gallery="${this.sectionId}"]`);
      this.sticky = document.querySelector(`[data-sticky-bar="${this.sectionId}"]`);
      this.optionInputs.forEach(i => on(i, 'change', () => this.update(true)));
      $$('[data-tier]', root).forEach(t => on(t, 'click', () => this.setTier(t)));
      on(this.qty, 'change', () => this.updateTiers());
      this.update(false); this.setupSticky(); this.updateTiers();
    }
    selected() {
      const idx = [...new Set(this.optionInputs.map(i => i.dataset.optionIndex))];
      return idx.map(i => { const c = this.optionInputs.find(el => el.dataset.optionIndex === i && el.checked); return c ? c.value : null; });
    }
    currentVariant() {
      if (!this.optionInputs.length) return this.variants[0];
      const sel = this.selected();
      return this.variants.find(v => v.options.every((o, i) => o === sel[i]));
    }
    update(pushUrl) {
      const v = this.currentVariant();
      this.markAvailability();
      // Selected value labels
      this.selected().forEach((val, i) => { const el = $(`[data-selected-value="${i}"]`, this.root); if (el) el.textContent = val || ''; });
      if (!v) { this.setButton(false, T.strings.unavailable); return; }
      if (this.idInput) this.idInput.value = v.id;
      if (this.price) this.price.textContent = formatMoney(v.price);
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      if (this.compare) { this.compare.hidden = !onSale; this.compare.textContent = onSale ? formatMoney(v.compare_at_price) : ''; }
      if (this.price) this.price.classList.toggle('price__sale', !!onSale);
      if (this.savePct) { this.savePct.hidden = !onSale; if (onSale) this.savePct.textContent = `-${Math.round((1 - v.price / v.compare_at_price) * 100)} %`; }
      if (this.unit) {
        if (v.unit_price) { this.unit.hidden = false; const m = v.unit_price_measurement || {}; this.unit.textContent = `${this.unit.dataset.prefix || ''} ${formatMoney(v.unit_price)}/${m.reference_value > 1 ? m.reference_value : ''}${m.reference_unit || ''}`.trim(); }
        else this.unit.hidden = true;
      }
      this.setButton(v.available, v.available ? (this.submit ? this.submit.dataset.labelAvailable : '') : T.strings.soldOut);
      if (this.stock) {
        const low = v.available && v.inventory_management && v.inventory_quantity > 0 && v.inventory_quantity <= Number(this.stock.dataset.lowThreshold || 5);
        const preorder = v.available && v.inventory_management && v.inventory_quantity <= 0;
        this.stock.classList.toggle('stock--low', !!low); this.stock.classList.toggle('stock--out', !v.available);
        const txt = $('[data-stock-text]', this.stock);
        if (txt) txt.textContent = !v.available ? T.strings.soldOut : preorder ? T.strings.preorder : low ? T.strings.lowStock : (this.stock.dataset.textAvailable || T.strings.inStock);
      }
      if (pushUrl && this.root.dataset.updateUrl === 'true' && history.replaceState) { const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u); }
      if (v.featured_media && this.gallery) this.showMedia(v.featured_media.id);
      if (this.sticky) {
        const p = $('[data-sticky-price]', this.sticky); if (p) p.textContent = formatMoney(v.price);
        const b = $('[data-sticky-submit]', this.sticky); if (b) { b.disabled = !v.available; b.textContent = v.available ? b.dataset.labelAvailable : T.strings.soldOut; }
      }
      this.updateTiers();
    }
    setButton(enabled, label) { if (!this.submit) return; this.submit.disabled = !enabled; if (label) this.submit.textContent = label; }
    markAvailability() {
      // For each option value, check if any variant with current other selections is available
      const sel = this.selected();
      this.optionInputs.forEach(input => {
        const i = Number(input.dataset.optionIndex);
        const match = this.variants.some(v => v.options.every((o, j) => j === i ? o === input.value : (sel[j] === null || o === sel[j])));
        const available = this.variants.some(v => v.available && v.options.every((o, j) => j === i ? o === input.value : (sel[j] === null || o === sel[j])));
        input.closest('.option-pill,.option-swatch') && input.closest('.option-pill,.option-swatch').classList.toggle('is-unavailable', !available || !match);
      });
    }
    showMedia(mediaId) {
      const slide = this.gallery.querySelector(`[data-media-id="${mediaId}"]`);
      const carousel = this.gallery.querySelector('carousel-slider');
      if (slide && carousel) { const idx = $$('.carousel__slide', carousel).indexOf(slide); if (idx > -1) carousel.goTo(idx); }
    }
    setTier(t) {
      $$('[data-tier]', this.root).forEach(x => x.classList.toggle('is-active', x === t));
      if (this.qty) { this.qty.value = t.dataset.tier; }
    }
    updateTiers() {
      const tiers = $$('[data-tier]', this.root); if (!tiers.length || !this.qty) return;
      const q = Number(this.qty.value || 1);
      tiers.forEach(t => t.classList.toggle('is-active', Number(t.dataset.tier) === q));
    }
    setupSticky() {
      if (!this.sticky || !this.submit || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([en]) => this.sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0), { threshold: 0 });
      io.observe(this.submit);
      on($('[data-sticky-submit]', this.sticky), 'click', () => { this.submit.click(); });
    }
  }
  window.theme.ProductForm = ProductForm;
  const initProductForms = (root = document) => $$('[data-product-form]', root).forEach(el => new ProductForm(el));
  initProductForms();
  document.addEventListener('shopify:section:load', (e) => { initProductForms(e.target); });

  /* ---------- Gallery: thumbs + hotspots ---------- */
  $$('[data-gallery]').forEach(g => {
    const carousel = $('carousel-slider', g); const thumbs = $$('[data-thumb]', g);
    thumbs.forEach((t, i) => on(t, 'click', () => carousel.goTo(i)));
    on(carousel, 'carousel:change', (e) => {
      const w = carousel.slideWidth(); const idx = w ? Math.round(carousel.track.scrollLeft / w) : 0;
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
      thumbs[idx] && thumbs[idx].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    });
  });
  on(document, 'click', (e) => {
    const b = e.target.closest('[data-hotspot-toggle]');
    if (b) { const p = b.nextElementSibling; const open = p.hidden; $$('.hotspot__panel').forEach(x => x.hidden = true); p.hidden = !open; b.setAttribute('aria-expanded', String(open)); return; }
    if (!e.target.closest('.hotspot')) $$('.hotspot__panel').forEach(x => x.hidden = true);
  });

  /* ---------- Size guide modal ---------- */
  on(document, 'click', (e) => {
    const b = e.target.closest('[data-modal-open]'); if (b) { e.preventDefault(); openDrawer(b.dataset.modalOpen); }
  });

  /* =========================================================
     Shop the look
     ========================================================= */
  $$('[data-look]').forEach(look => {
    const pins = $$('[data-look-pin]', look), items = $$('[data-look-item]', look);
    const activate = (id) => { pins.forEach(p => p.classList.toggle('is-active', p.dataset.lookPin === id)); items.forEach(i => i.classList.toggle('is-active', i.dataset.lookItem === id)); };
    pins.forEach(p => { on(p, 'mouseenter', () => activate(p.dataset.lookPin)); on(p, 'click', () => { activate(p.dataset.lookPin); const it = items.find(i => i.dataset.lookItem === p.dataset.lookPin); it && it.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }); });
    items.forEach(i => on(i, 'mouseenter', () => activate(i.dataset.lookItem)));
  });

  /* =========================================================
     Collection filters (AJAX)
     ========================================================= */
  const collection = $('[data-collection]');
  if (collection) {
    const sectionId = collection.dataset.sectionId;
    const form = $('[data-filter-form]');
    const results = $('[data-collection-results]');
    const render = async (url, push = true) => {
      results.classList.add('is-loading');
      try {
        const u = new URL(url, location.origin); u.searchParams.set('section_id', sectionId);
        const html = await (await fetch(u)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['[data-collection-results]', '[data-filter-form]', '[data-active-filters]', '[data-results-count]'].forEach(sel => {
          const fresh = $(sel, doc), cur = $(sel); if (fresh && cur) cur.replaceWith(fresh);
        });
        if (push) history.pushState({}, '', url);
        window.theme.observeReveal && window.theme.observeReveal($('[data-collection-results]'));
        const resEl = $('[data-collection-results]'); resEl && resEl.classList.remove('is-loading');
      } catch (err) { results.classList.remove('is-loading'); toast(err.message); }
    };
    const submitFilters = debounce(() => {
      const f = $('[data-filter-form]'); const fd = new FormData(f);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      const sort = $('[data-sort-select]'); if (sort && sort.value) params.set('sort_by', sort.value);
      render(`${location.pathname}?${params.toString()}`);
    }, 300);
    on(document, 'change', (e) => { if (e.target.closest('[data-filter-form]')) submitFilters(); if (e.target.closest('[data-sort-select]')) submitFilters(); });
    on(document, 'submit', (e) => { if (e.target.closest('[data-filter-form]')) { e.preventDefault(); submitFilters(); } });
    on(document, 'click', (e) => {
      const a = e.target.closest('[data-filter-remove], [data-filter-clear], .pagination a');
      if (!a) return; e.preventDefault(); render(a.href); if (a.closest('.pagination')) window.scrollTo({ top: collection.offsetTop - 80, behavior: 'smooth' });
    });
    on(document, 'click', (e) => { if (e.target.closest('[data-filter-apply]')) { e.preventDefault(); closeDrawer(); } });
    on(window, 'popstate', () => render(location.href, false));
  }

  /* =========================================================
     Predictive search
     ========================================================= */
  $$('[data-predictive-search]').forEach(input => {
    const wrap = input.closest('form'); let box = $('.search-suggest', wrap.parentElement);
    if (!box) { box = document.createElement('div'); box.className = 'search-suggest'; box.hidden = true; wrap.parentElement.appendChild(box); }
    const minChars = parseInt(input.dataset.minChars || '3', 10);
    const popular = (input.dataset.popular || '').split(',').map(t => t.trim()).filter(Boolean).slice(0, 4);
    const hide = () => box.hidden = true;
    const esc = (str) => String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const searchUrl = (q) => `${T.routes.search}?q=${encodeURIComponent(q)}&type=product`;
    const showPopular = () => {
      if (!popular.length) { hide(); return; }
      box.innerHTML = `<div class="search-suggest__label">Beliebte Suchen</div><div class="search-suggest__chips">${popular.map(t => `<a class="search-suggest__chip" href="${searchUrl(t)}">${esc(t)}</a>`).join('')}</div>`;
      box.hidden = false;
    };
    const run = debounce(async () => {
      const q = input.value.trim();
      if (q.length < minChars) { showPopular(); return; }
      box.classList.add('is-loading');
      try {
        const res = await fetch(`${T.routes.predictive}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag`);
        const data = await res.json();
        const products = data.resources.results.products || [];
        if (!products.length) { box.innerHTML = `<div class="search-suggest__empty">${(T.strings.noResults || '').replace('__terms__', esc(q))}</div>` + (popular.length ? `<div class="search-suggest__label">Beliebte Suchen</div><div class="search-suggest__chips">${popular.map(t => `<a class="search-suggest__chip" href="${searchUrl(t)}">${esc(t)}</a>`).join('')}</div>` : ''); box.hidden = false; return; }
        box.innerHTML = `<div class="search-suggest__label">${T.strings.products}</div><div class="search-suggest__list">` + products.map(p => `<a class="search-suggest__item" href="${p.url}">${p.image ? `<img src="${p.image}&width=120" alt="" width="56" height="56" loading="lazy">` : '<span class="search-suggest__noimg"></span>'}<div><strong>${esc(p.title)}</strong><span>${p.price ? formatMoney(Math.round(parseFloat(p.price) * 100)) : ''}</span></div></a>`).join('') + `</div><a class="search-suggest__all btn btn--outline btn--small" href="${searchUrl(q)}">${T.strings.viewAll}</a>`;
        box.hidden = false;
      } catch (e) { hide(); } finally { box.classList.remove('is-loading'); }
    }, 200);
    on(input, 'input', run);
    on(input, 'focus', () => { const q = input.value.trim(); if (q.length >= minChars && box.innerHTML) box.hidden = false; else showPopular(); });
    on(input, 'keydown', (e) => { if (e.key === 'Escape') hide(); });
    on(document, 'click', (e) => { if (!wrap.parentElement.contains(e.target)) hide(); });
  });

  /* =========================================================
     Newsletter (AJAX-ish feedback)
     ========================================================= */
  $$('form[data-newsletter]').forEach(f => {
    const params = new URLSearchParams(location.search);
    if (params.get('customer_posted') === 'true') { const m = $('[data-newsletter-success]', f); if (m) { m.hidden = false; f.scrollIntoView({ block: 'center' }); } }
  });

  /* ---------- Section editor hooks ---------- */
  document.addEventListener('shopify:section:load', (e) => { $$('carousel-slider', e.target).forEach(c => c.connectedCallback && c.connectedCallback()); });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.carousel__slide'); const car = e.target.closest('carousel-slider');
    if (slide && car) car.goTo($$('.carousel__slide', car).indexOf(slide));
    const drawer = e.target.closest('.drawer'); if (drawer) openDrawer(drawer.id);
  });
  document.addEventListener('shopify:section:select', (e) => { const drawer = $('.drawer', e.target); if (drawer) openDrawer(drawer.id); });
  document.addEventListener('shopify:section:deselect', () => closeDrawer());
})();
